// src/pages/Fornecedores.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  query,
  orderBy
} from 'firebase/firestore';
import { useEmpresaId } from '../context/EmpresaContext';
import Swal from 'sweetalert2';

import ModalNovoFornecedor from '../components/ModalNovoFornecedor';
import {
  PlusCircle, Search, Truck, User, Phone, FileText, Pencil, Trash2
} from 'lucide-react';

const db = getFirestore();

/* ===== Botão tom-sobre-tom (ícone sem fundo) ===== */
const toneStyles = (tone) => {
  switch (tone) {
    case 'primary':
      return 'text-blue-800 bg-blue-100 hover:bg-blue-200 dark:text-blue-300 dark:bg-blue-900/50 dark:hover:bg-blue-900';
    case 'neutral':
      return 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700';
    case 'danger':
      return 'text-red-700 bg-red-100 hover:bg-red-200 dark:text-red-300 dark:bg-red-900/50 dark:hover:bg-red-900';
    default:
      return 'text-gray-700 bg-gray-100 hover:bg-gray-200 dark:text-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700';
  }
};
const ActionBtn = ({ tone = 'neutral', icon: Icon, className = '', disabled, children, ...props }) => {
  const base = `inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md ${toneStyles(tone)} ${
    disabled ? 'opacity-60 cursor-not-allowed' : ''
  } ${className}`;
  return (
    <button className={base} disabled={disabled} {...props}>
      {Icon && <Icon size={16} className="text-current" />} {children}
    </button>
  );
};

export default function Fornecedores() {
  const empresaId = useEmpresaId();

  const [fornecedores, setFornecedores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [fornecedorEmEdicao, setFornecedorEmEdicao] = useState(null);
  const [busca, setBusca] = useState('');

  useEffect(() => {
    if (empresaId) fetchFornecedores(empresaId);
    else setLoading(false);
  }, [empresaId]);

  const colFornecedores = (id) => collection(db, 'empresas', id, 'fornecedores');
  const refFornecedor = (id, fornecedorId) => doc(db, 'empresas', id, 'fornecedores', fornecedorId);

  const fetchFornecedores = async (id) => {
    setLoading(true);
    try {
      const q = query(colFornecedores(id), orderBy('nome'));
      const snapshot = await getDocs(q);
      setFornecedores(snapshot.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (error) {
      console.error('Erro ao buscar fornecedores:', error);
      Swal.fire('Erro', 'Não foi possível carregar os fornecedores.', 'error');
    }
    setLoading(false);
  };

  const handleNovoFornecedor = () => {
    setFornecedorEmEdicao(null);
    setIsModalOpen(true);
  };

  const handleEditarFornecedor = (fornecedor) => {
    setFornecedorEmEdicao(fornecedor);
    setIsModalOpen(true);
  };

  const handleSalvarFornecedor = async (form) => {
    if (!empresaId) return;
    try {
      if (fornecedorEmEdicao) {
        await updateDoc(refFornecedor(empresaId, fornecedorEmEdicao.id), form);
        Swal.fire('Sucesso!', 'Fornecedor atualizado com sucesso.', 'success');
      } else {
        await addDoc(colFornecedores(empresaId), { ...form });
        Swal.fire('Sucesso!', 'Fornecedor cadastrado com sucesso.', 'success');
      }
      setIsModalOpen(false);
      fetchFornecedores(empresaId);
    } catch (error) {
      console.error('Erro ao salvar fornecedor:', error);
      Swal.fire('Erro', 'Não foi possível salvar os dados do fornecedor.', 'error');
    }
  };

  const handleExcluirFornecedor = async (id) => {
    const result = await Swal.fire({
      title: 'Tem a certeza?',
      text: 'Esta ação não pode ser desfeita!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonText: 'Cancelar',
      confirmButtonText: 'Sim, excluir!'
    });
    if (result.isConfirmed && empresaId) {
      try {
        await deleteDoc(refFornecedor(empresaId, id));
        Swal.fire('Excluído!', 'O fornecedor foi removido.', 'success');
        fetchFornecedores(empresaId);
      } catch (error) {
        console.error('Erro ao excluir fornecedor:', error);
        Swal.fire('Erro', 'Não foi possível excluir o fornecedor.', 'error');
      }
    }
  };

  const fornecedoresFiltrados = useMemo(() => {
    if (!busca) return fornecedores;
    const buscaLower = busca.toLowerCase();
    return fornecedores.filter(
      (f) =>
        (f.nome || '').toLowerCase().includes(buscaLower) ||
        (f.contatoNome || '').toLowerCase().includes(buscaLower)
    );
  }, [fornecedores, busca]);

  return (
    <div className="p-6 md:p-8 bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
        <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100">Fornecedores</h1>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative flex-grow">
            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por nome..."
              className="border px-3 py-2 pl-10 rounded-lg w-full bg-white dark:bg-gray-800 dark:border-gray-700"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <button
            onClick={handleNovoFornecedor}
            className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
          >
            <PlusCircle size={18} /> Novo Fornecedor
          </button>
        </div>
      </div>

      {loading ? (
        <p className="text-center text-gray-500 py-10">A carregar fornecedores...</p>
      ) : fornecedoresFiltrados.length === 0 ? (
        <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-xl">
          <Truck size={48} className="mx-auto text-gray-400 mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200">
            Nenhum fornecedor encontrado
          </h3>
          <p className="text-gray-500 mt-2">Clique em &quot;Novo Fornecedor&quot; para começar.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {fornecedoresFiltrados.map((fornecedor) => (
            <CardFornecedor
              key={fornecedor.id}
              fornecedor={fornecedor}
              onEditar={() => handleEditarFornecedor(fornecedor)}
              onExcluir={() => handleExcluirFornecedor(fornecedor.id)}
            />
          ))}
        </div>
      )}

      <ModalNovoFornecedor
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSalvarFornecedor}
        fornecedorEmEdicao={fornecedorEmEdicao}
      />
    </div>
  );
}

/* ===== Card ===== */
const CardFornecedor = ({ fornecedor, onEditar, onExcluir }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md flex flex-col transition hover:shadow-xl hover:-translate-y-1">
    {/* Cabeçalho padronizado */}
    <div className="p-4 flex justify-between items-start">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-blue-100 dark:bg-blue-900/50 rounded-md">
          <Truck className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h3 className="font-bold text-lg text-gray-800 dark:text-gray-100">{fornecedor.nome}</h3>
        </div>
      </div>
    </div>

    {/* Corpo */}
    <div className="p-4 space-y-3 text-sm flex-grow border-t border-b dark:border-gray-700">
      <InfoItem icone={User} label="Contato:" texto={fornecedor.contatoNome || 'Não informado'} />
      <InfoItem icone={Phone} label="Telefone:" texto={fornecedor.telefone || 'Não informado'} />
      <InfoItem icone={FileText} label="Produtos/Obs:" texto={fornecedor.obs || 'Nenhuma'} />
    </div>

    {/* Rodapé com botões padronizados */}
    <div className="p-3 flex items-center justify-end gap-2 bg-gray-50 dark:bg-gray-800/50 rounded-b-xl">
      <ActionBtn tone="neutral" icon={Pencil} onClick={onEditar}>
        Editar
      </ActionBtn>
      <ActionBtn tone="danger" icon={Trash2} onClick={onExcluir}>
        Excluir
      </ActionBtn>
    </div>
  </div>
);

/* ===== Linha de informação ===== */
const InfoItem = ({ icone: Icone, label, texto }) => (
  <div className="flex items-start gap-3 text-gray-600 dark:text-gray-300">
    <Icone size={16} className="text-gray-400 mt-0.5 flex-shrink-0" />
    <div>
      <span className="font-semibold text-gray-700 dark:text-gray-200">{label}</span>
      <span className="ml-1 break-words">{texto}</span>
    </div>
  </div>
);
